import { isEqual } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { TileNode } from '@/components/diagrams/FlowChart/types';
import { BdgMissingBeneficiaryTier } from '@/modules/entityDiagram/bdgActiveTier';
import { RecipientDispositions } from '@/modules/entityDiagram/getDispositionsVizFromEntity';
import {
  AfterDeath,
  EntityInEstateStatus,
  EntityKind,
  TestamentaryEntityKind,
} from '@/types/schema';

import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';

// Recipient shape enriched for diagram rendering
export type DiagramDispositionsFormRecipient =
  DispositiveProvisionsFormRecipient & {
    displayName: string;
    entityKind?: EntityKind;
    testamentaryEntityKind?: TestamentaryEntityKind;
    isCharitableOrganization?: boolean;
    inEstateStatus?: EntityInEstateStatus;
    subtypeId?: string;
  };

export interface DiagramDispositionsFormState {
  firstDeath: DiagramDispositionsFormRecipient[];
  secondDeath: DiagramDispositionsFormRecipient[] | null;
  // BDG-only signal from the live form: which beneficiary tier is missing and
  // should be requested in the diagram's "Please specify a {tier} beneficiary"
  // warning (so it tracks unsaved edits, matching the inline tier text).
  bdgRootMissingBeneficiaryTier?: BdgMissingBeneficiaryTier;
}

/**
 * Live overlay for a BDG entity edited as a *non-root* target — i.e. the
 * form is open for an entity that the diagram is showing as a downstream
 * recipient of a different root entity. Keyed by that entity's id so it
 * overrides the server-resolved dispositions for that entity only, leaving
 * the root's `diagramDispositionsFormState` overlay untouched. Downstream
 * entities render their active tier in `secondDeath`; `firstDeath` is empty.
 */
export interface NonRootRecipientOverride {
  entityId: string;
  firstDeath: DiagramDispositionsFormRecipient[];
  secondDeath: DiagramDispositionsFormRecipient[];
  /** Live-form analogue of `ResolvedDispositions.missingBeneficiaryTier`
   * for the edited entity's tile warning. */
  missingBeneficiaryTier?: BdgMissingBeneficiaryTier;
}

export interface SelectedNode {
  pulid: string;
  afterDeath: AfterDeath;
  node: TileNode | null;
}

/**
 * BDG metadata published by the diagram so form-side hooks and out-of-tree
 * UI (e.g., the death-order Switch above the form diagram) can read and
 * mutate the active scenario without re-fetching the entity. Null when not
 * a BDG-aware entity.
 */
export interface BdgDiagramSyncState {
  /**
   * "Default trigger" client (account holder / first policy holder).
   * Null for survivorship policies or single-client households. Drives
   * the OFF position of the death-order Switch only — tier-availability
   * logic must use `triggeringClientIds`, not this single id.
   */
  defaultTriggerClientId: string | null;
  /**
   * Full list of clients whose death triggers distribution (e.g. all
   * policy holders on a 2-holder policy, or the single account owner).
   * Published by the diagram so form-side hooks can replay the resolver's
   * tier decision without re-deriving it from a lossy summary.
   */
  triggeringClientIds: string[];
  /** Current "who dies first" selection from the diagram. */
  selectedPrimaryClientId: string;
  /** True when any policy on the account has `survivorship=true`. */
  isSurvivorship: boolean;
  /** Setter exposed so an external Switch UI can drive the scenario. */
  setSelectedPrimaryClientId: (id: string) => void;
}

interface DispositiveDiagramSyncContextValue {
  diagramDispositionsFormState: DiagramDispositionsFormState | null;
  setDiagramDispositionsFormState: (d: DiagramDispositionsFormState) => void;
  recipientDispositions: RecipientDispositions;
  setRecipientDispositions: (
    id: string,
    dispositions: RecipientDispositions[string]
  ) => void;
  recipientDispositionWarnings: Record<string, boolean>;
  setRecipientDispositionWarning: (
    id: string,
    hasDispositions: boolean
  ) => void;
  isLoadingRecipientDispositions: boolean;
  setIsLoadingRecipientDispositions: (isLoading: boolean) => void;

  /**
   * Currently selected node in the entity diagram. `pulid` is the entity id and
   * `afterDeath` indicates the diagram section the node belongs to.
   */
  selectedNode: SelectedNode | null;
  setSelectedNode: (node: SelectedNode | null) => void;

  /**
   * The target whose form is actually shown in the split screen. Set only
   * after a tile-click save succeeds, so the root form is not unmounted
   * before it can persist its edits. `selectedNode` updates immediately for
   * diagram highlighting; this lags it by the save.
   */
  committedTargetNode: SelectedNode | null;
  setCommittedTargetNode: (node: SelectedNode | null) => void;

  /** BDG metadata published by the diagram, consumed by the BDG form sync hook. */
  bdgDiagramSyncState: BdgDiagramSyncState | null;
  setBdgDiagramSyncState: (state: BdgDiagramSyncState | null) => void;

  /**
   * Live overlay for a BDG entity edited as a non-root target (see
   * {@link NonRootRecipientOverride}). Null when editing the root entity or
   * when no BDG form is open. Threaded into the diagram alongside
   * `externalDispositions`, but keyed by the edited entity's id.
   */
  nonRootRecipientOverride: NonRootRecipientOverride | null;
  setNonRootRecipientOverride: (
    override: NonRootRecipientOverride | null
  ) => void;

  /**
   * Restore all sync state to its initial values. The provider is mounted
   * above the death-order-keyed split-screen layout, so any death-order or
   * section switch must call this — state left behind (e.g.
   * `recipientDispositions` resolved for the previous first-death client) is
   * otherwise read by the next diagram instance as if it were current.
   * Excludes `bdgDiagramSyncState`, which is owned by the mounted diagram
   * and cleared by its own effect cleanup.
   */
  resetDiagramSyncState: () => void;
}

const DispositiveDiagramSyncContext = createContext<
  DispositiveDiagramSyncContextValue | undefined
>(undefined);

export function DispositiveDiagramSyncProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [diagramDispositionsFormState, setDiagramDispositionsFormState] =
    useState<DiagramDispositionsFormState | null>(null);

  /**
   * Map<recipientEntityId, dispositions> used for second-hop branches. We keep
   * this outside the main `dispositions` object to avoid accidentally mixing
   * recipient scenarios with the root scenario.
   */
  const [recipientDispositions, updateRecipientDispositions] =
    useState<RecipientDispositions>({});
  // Warning evidence is not proof that the recipient's full query has loaded.
  const [recipientDispositionWarnings, updateRecipientDispositionWarnings] =
    useState<Record<string, boolean>>({});
  const setRecipientDispositionWarning = useCallback(
    (id: string, hasDispositions: boolean) => {
      updateRecipientDispositionWarnings((prev) =>
        prev[id] === hasDispositions ? prev : { ...prev, [id]: hasDispositions }
      );
    },
    []
  );

  // Track the currently selected node in the entity diagram (Pulid + afterDeath)
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);

  // Target whose form is shown; committed only after a tile-click save resolves.
  const [committedTargetNode, setCommittedTargetNode] =
    useState<SelectedNode | null>(null);

  const setRecipientDispositions = useCallback(
    (id: string, dispositions: RecipientDispositions[string]) => {
      updateRecipientDispositions((prev) => {
        if (isEqual(prev[id], dispositions)) {
          return prev; // no change
        }
        return { ...prev, [id]: dispositions };
      });
    },
    []
  );

  const [isLoadingRecipientDispositions, setIsLoadingRecipientDispositions] =
    useState(true);

  const [bdgDiagramSyncState, setBdgDiagramSyncState] =
    useState<BdgDiagramSyncState | null>(null);

  const [nonRootRecipientOverride, setNonRootRecipientOverride] =
    useState<NonRootRecipientOverride | null>(null);

  const resetDiagramSyncState = useCallback(() => {
    setDiagramDispositionsFormState(null);
    updateRecipientDispositions({});
    updateRecipientDispositionWarnings({});
    setIsLoadingRecipientDispositions(true);
    setSelectedNode(null);
    setCommittedTargetNode(null);
    setNonRootRecipientOverride(null);
  }, []);

  return (
    <DispositiveDiagramSyncContext.Provider
      value={{
        diagramDispositionsFormState,
        setDiagramDispositionsFormState,
        recipientDispositions,
        setRecipientDispositions,
        recipientDispositionWarnings,
        setRecipientDispositionWarning,
        isLoadingRecipientDispositions,
        setIsLoadingRecipientDispositions,
        selectedNode,
        setSelectedNode,
        committedTargetNode,
        setCommittedTargetNode,
        bdgDiagramSyncState,
        setBdgDiagramSyncState,
        nonRootRecipientOverride,
        setNonRootRecipientOverride,
        resetDiagramSyncState,
      }}
    >
      {children}
    </DispositiveDiagramSyncContext.Provider>
  );
}

export function useDispositiveDiagramSyncContext() {
  const ctx = useContext(DispositiveDiagramSyncContext);
  if (!ctx) {
    throw new Error(
      'useDispositiveDiagramSyncContext must be used within DispositiveDiagramSyncProvider'
    );
  }
  return ctx;
}

/**
 * Same as `useDispositiveDiagramSyncContext`, but returns `undefined` instead of
 * throwing when the provider is not present. Useful when a component can be
 * rendered both inside and outside the provider tree.
 */
export function useOptionalDispositiveDiagramSyncContext() {
  return useContext(DispositiveDiagramSyncContext);
}
